import type { z } from 'zod'

import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  actionRequestCommandResultSchema,
  interactionActionRequestListSchema,
  interactionActionRequestSchema,
  interactionOperationalCommandResultSchema,
  interactionOperationalQuerySchema,
  interactionOperationalStateSchema,
  type InteractionActionRequest,
  type InteractionActionRequestList,
  type InteractionOperationalState,
} from './interaction-operational-contracts'

export type InteractionOperationalRepository = {
  query: (
    tenantId: string,
    aggregateIds: string[],
    signal?: AbortSignal
  ) => Promise<InteractionOperationalState[]>
  markRead: (
    tenantId: string,
    aggregateId: string,
    expectedRevision: number,
    expectedActivityAtMs: number
  ) => Promise<InteractionOperationalState>
  markReviewed: (
    tenantId: string,
    aggregateId: string,
    expectedRevision: number,
    expectedActivityAtMs: number,
    reviewed?: boolean
  ) => Promise<InteractionOperationalState>
  setStarred: (
    tenantId: string,
    aggregateId: string,
    expectedRevision: number,
    starred: boolean
  ) => Promise<InteractionOperationalState>
  // Action completion is separate from review: it resolves or reopens the
  // aggregate's Action Requests and never touches the review record.
  listActionRequests?: (
    tenantId: string,
    aggregateId: string,
    signal?: AbortSignal
  ) => Promise<InteractionActionRequestList>
  resolveActionRequest?: (
    tenantId: string,
    actionRequestId: string,
    expectedRevision: number
  ) => Promise<InteractionActionRequest>
  reopenActionRequest?: (
    tenantId: string,
    actionRequestId: string,
    expectedRevision: number
  ) => Promise<InteractionActionRequest>
}

function actionRequestPath(tenantId: string, actionRequestId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/action-requests/${encodeURIComponent(actionRequestId)}`
}

function base(tenantId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates`
}

function commandState(value: Record<string, unknown>) {
  const state = { ...value }
  delete state.replayClassification
  return interactionOperationalStateSchema.parse(state)
}

function withExplicitReopening(
  value: z.infer<typeof interactionActionRequestSchema>
): InteractionActionRequest {
  return {
    ...value,
    reopenedAtMs: value.reopenedAtMs ?? null,
    reopenable: value.reopenable === true,
  }
}

function actionRequestState(value: Record<string, unknown>) {
  const state = { ...value }
  delete state.replayClassification
  return withExplicitReopening(interactionActionRequestSchema.parse(state))
}

export function createInteractionOperationalRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): InteractionOperationalRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    async query(tenantId, aggregateIds, signal) {
      const result = await client.request(
        `${base(tenantId)}/operational-state/query`,
        {
          method: 'POST',
          body: { aggregateIds },
          signal,
          responseSchema: interactionOperationalQuerySchema,
        }
      )
      return result.items
    },
    async markRead(
      tenantId,
      aggregateId,
      expectedRevision,
      expectedActivityAtMs
    ) {
      const result = await client.request(
        `${base(tenantId)}/${encodeURIComponent(aggregateId)}/read`,
        {
          method: 'POST',
          body: { expectedRevision, expectedActivityAtMs },
          responseSchema: interactionOperationalCommandResultSchema,
        }
      )
      return commandState(result)
    },
    async markReviewed(
      tenantId,
      aggregateId,
      expectedRevision,
      expectedActivityAtMs,
      reviewed
    ) {
      const result = await client.request(
        `${base(tenantId)}/${encodeURIComponent(aggregateId)}/review`,
        {
          method: 'POST',
          body:
            reviewed === undefined
              ? { expectedRevision, expectedActivityAtMs }
              : { expectedRevision, expectedActivityAtMs, reviewed },
          responseSchema: interactionOperationalCommandResultSchema,
        }
      )
      return commandState(result)
    },
    async listActionRequests(tenantId, aggregateId, signal) {
      const result = await client.request(
        `${base(tenantId)}/${encodeURIComponent(aggregateId)}/action-requests`,
        {
          method: 'GET',
          signal,
          responseSchema: interactionActionRequestListSchema,
        }
      )
      return {
        items: result.items.map(withExplicitReopening),
        staffLifecycleEnabled: result.staffLifecycleEnabled === true,
      }
    },
    async resolveActionRequest(tenantId, actionRequestId, expectedRevision) {
      const result = await client.request(
        `${actionRequestPath(tenantId, actionRequestId)}/resolve`,
        {
          method: 'POST',
          body: { expectedRevision, resolutionType: 'STAFF_HANDLED' },
          responseSchema: actionRequestCommandResultSchema,
        }
      )
      return actionRequestState(result)
    },
    async reopenActionRequest(tenantId, actionRequestId, expectedRevision) {
      const result = await client.request(
        `${actionRequestPath(tenantId, actionRequestId)}/reopen`,
        {
          method: 'POST',
          body: { expectedRevision },
          responseSchema: actionRequestCommandResultSchema,
        }
      )
      return actionRequestState(result)
    },
    async setStarred(tenantId, aggregateId, expectedRevision, starred) {
      const result = await client.request(
        `${base(tenantId)}/${encodeURIComponent(aggregateId)}/star`,
        {
          method: 'PUT',
          body: { expectedRevision, starred },
          responseSchema: interactionOperationalCommandResultSchema,
        }
      )
      return commandState(result)
    },
  }
}
